<template>
  <SoAdminPage
    title="Email Throttles"
    subtitle="Manage per-template email throttle configs and review throttle events"
  >
    <SoWidget class="mb-6">
      <template #title>
        <div class="d-flex align-center justify-space-between">
          <span>Throttle Configurations</span>
          <SoButton
            title="Add Config"
            type="primary"
            icon="plus"
            data-testid="create-config-btn"
            @click="openCreateDialog"
          />
        </div>
      </template>
      <SoDataTable
        :headers="configHeaders"
        :items="configs"
        :loading="configsQuery?.isFetching ?? false"
        :searchable="false"
        :has-pagination="false"
        data-testid="configs-table"
      >
        <template #[`item.enabled`]="{ internalItem }">
          <SoChip
            :type="
              internalItem.raw.enabled ? SoChipType.SUCCESS : SoChipType.DEFAULT
            "
            :text="internalItem.raw.enabled ? 'Enabled' : 'Disabled'"
          />
        </template>
        <template #[`item.windowSeconds`]="{ internalItem }">
          {{ formatWindow(internalItem.raw.windowSeconds as number) }}
        </template>
        <template #itemActions="{ item }">
          <SoButton
            icon="pencil"
            data-testid="edit-config-btn"
            @click="openEditDialog(item as IThrottleConfig)"
          />
        </template>
      </SoDataTable>
    </SoWidget>

    <SoWidget>
      <template #title>
        <div class="d-flex align-center justify-space-between">
          <span>Throttle Events</span>
          <SoSelect
            v-model="eventStatusFilter"
            :items="statusFilterOptions"
            label="Status"
            clearable
            style="max-width: 200px"
            data-testid="event-status-filter"
          />
        </div>
      </template>
      <SoDataTable
        :headers="eventHeaders"
        :items="events"
        :loading="eventsQuery?.isFetching ?? false"
        :searchable="false"
        :has-pagination="false"
        data-testid="events-table"
      >
        <template #[`item.status`]="{ internalItem }">
          <SoChip
            :type="
              statusChipType(internalItem.raw.status as ThrottleEventStatus)
            "
            :text="internalItem.raw.status as string"
          />
        </template>
        <template #[`item.windowStart`]="{ internalItem }">
          {{ formatDate(internalItem.raw.windowStart) }}
        </template>
        <template #[`item.resumedBy`]="{ internalItem }">
          <span
            v-if="internalItem.raw.resumedBy"
            class="text-caption text-medium-emphasis"
          >
            {{ internalItem.raw.resumedBy }}
            <span v-if="internalItem.raw.resumedAt">
              · {{ formatDate(internalItem.raw.resumedAt) }}</span
            >
          </span>
          <span
            v-else-if="internalItem.raw.discardedBy"
            class="text-caption text-medium-emphasis"
          >
            {{ internalItem.raw.discardedBy }}
            <span v-if="internalItem.raw.discardedAt">
              · {{ formatDate(internalItem.raw.discardedAt) }}</span
            >
          </span>
          <span v-else>—</span>
        </template>
        <template #itemActions="{ item }">
          <template v-if="item.status === ThrottleEventStatus.ACTIVE">
            <SoButton
              title="Resume"
              type="success"
              data-testid="resume-btn"
              @click="confirmResume(item as IThrottleEvent)"
            />
            <SoButton
              title="Discard"
              type="danger"
              data-testid="discard-btn"
              @click="confirmDiscard(item as IThrottleEvent)"
            />
          </template>
        </template>
      </SoDataTable>
    </SoWidget>
  </SoAdminPage>

  <SoDialog
    v-model="showConfigDialog"
    :card-title="editingConfig ? 'Edit Config' : 'Add Throttle Config'"
    :max-width="480"
    data-testid="config-dialog"
  >
    <p
      v-if="!editingConfig && deepLinkCommunicationName"
      class="text-medium-emphasis mb-2"
      data-testid="deep-link-template-name"
    >
      For template: <strong>{{ deepLinkCommunicationName }}</strong>
    </p>
    <SoTextField
      v-if="!editingConfig"
      v-model.number="configForm.communicationId"
      label="Communication ID"
      type="number"
      :min="1"
      data-testid="communication-id-field"
    />
    <SoTextField
      v-model.number="configForm.maxPerWindow"
      label="Max emails per window"
      type="number"
      :min="0"
      data-testid="max-per-window-field"
    />
    <SoTextField
      v-model.number="configForm.windowSeconds"
      label="Window (seconds)"
      type="number"
      :min="1"
      data-testid="window-seconds-field"
    />
    <SoToggle
      v-if="editingConfig"
      v-model="configForm.enabled"
      label="Enabled"
      data-testid="enabled-switch"
    />
    <div class="d-flex justify-end mt-4">
      <SoButton
        title="Cancel"
        class="mr-2"
        large
        @click="showConfigDialog = false"
      />
      <SoButton
        title="Save"
        type="success"
        large
        :loading="createMutation?.isPending || updateMutation?.isPending"
        :disabled="!isConfigFormValid"
        data-testid="save-config-btn"
        @click="saveConfig"
      />
    </div>
  </SoDialog>

  <SoDialog
    v-model="showResumeDialog"
    card-title="Resume throttle event?"
    :max-width="440"
    data-testid="resume-dialog"
  >
    <p>
      Throttled emails for
      <strong>{{ pendingEventAction?.communicationName }}</strong> will be
      requeued and sent. A bypass key will be set for
      {{ formatWindow(pendingEventAction?.windowSeconds) }} to prevent
      re-throttling.
    </p>
    <div class="d-flex justify-end mt-4">
      <SoButton
        title="Cancel"
        class="mr-2"
        large
        @click="showResumeDialog = false"
      />
      <SoButton
        title="Resume"
        type="success"
        large
        :loading="resumeMutation?.isPending"
        data-testid="confirm-resume-btn"
        @click="executeResume"
      />
    </div>
  </SoDialog>

  <SoDialog
    v-model="showDiscardDialog"
    card-title="Discard throttle event?"
    :max-width="440"
    data-testid="discard-dialog"
  >
    <p>
      Throttled emails for
      <strong>{{ pendingEventAction?.communicationName }}</strong> will be
      permanently dropped. This cannot be undone.
    </p>
    <div class="d-flex justify-end mt-4">
      <SoButton
        title="Cancel"
        class="mr-2"
        large
        @click="showDiscardDialog = false"
      />
      <SoButton
        title="Discard"
        type="danger"
        large
        :loading="discardMutation?.isPending"
        data-testid="confirm-discard-btn"
        @click="executeDiscard"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { Dayjs } from "dayjs"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { EmailThrottleService } from "@/services/admin/EmailThrottleService"
  import { EmailThrottleMutator } from "@/mutators/admin/EmailThrottleMutator"
  import { QueryResult } from "@/api/utils/types"
  import { IThrottleConfig } from "@/models/throttleConfig/throttleConfig.types"
  import {
    IThrottleEvent,
    ThrottleEventStatus,
  } from "@/models/throttleEvent/throttleEvent.types"
  import {
    ConfigFormValues,
    isConfigFormValid as checkConfigFormValid,
  } from "./helpers/validateConfigForm"

  const configHeaders: SoDataTableHeader[] = [
    { key: "communicationName", title: "Template" },
    { key: "maxPerWindow", title: "Limit" },
    { key: "windowSeconds", title: "Window" },
    { key: "enabled", title: "Status" },
    // no 'actions' entry — SoDataTable auto-appends it for the itemActions slot
  ]

  const eventHeaders: SoDataTableHeader[] = [
    { key: "communicationName", title: "Template" },
    { key: "windowStart", title: "Window Start" },
    { key: "emailsSent", title: "Sent" },
    { key: "emailsThrottled", title: "Throttled" },
    { key: "maxPerWindow", title: "Limit" },
    { key: "status", title: "Status" },
    { key: "resumedBy", title: "Actioned by" },
    // no 'actions' entry — SoDataTable auto-appends it for the itemActions slot
  ]

  const statusFilterOptions = [
    ThrottleEventStatus.ACTIVE,
    ThrottleEventStatus.RESUMED,
    ThrottleEventStatus.DISCARDED,
  ]

  export default defineComponent({
    components: {
      SoAdminPage,
      SoWidget,
      SoDataTable,
      SoChip,
      SoButton,
      SoDialog,
      SoTextField,
      SoToggle,
      SoSelect,
    },
    data() {
      return {
        configHeaders,
        eventHeaders,
        statusFilterOptions,
        SoChipType,
        ThrottleEventStatus,
        eventStatusFilter:
          ThrottleEventStatus.ACTIVE as ThrottleEventStatus | null,
        configsQuery: null as QueryResult<IThrottleConfig[]> | null,
        eventsQuery: null as QueryResult<IThrottleEvent[]> | null,
        showConfigDialog: false,
        deepLinkHandled: false,
        deepLinkCommunicationId: null as number | null,
        deepLinkCommunicationName: null as string | null,
        editingConfig: null as IThrottleConfig | null,
        configForm: {
          communicationId: null as number | null,
          maxPerWindow: null as number | null,
          windowSeconds: 3600,
          enabled: true,
        },
        showResumeDialog: false,
        showDiscardDialog: false,
        pendingEventAction: null as IThrottleEvent | null,
        createMutation: null as any,
        updateMutation: null as any,
        resumeMutation: null as any,
        discardMutation: null as any,
      }
    },
    computed: {
      configs(): SoDataTableItem[] {
        return (this.configsQuery?.data ?? []) as unknown as SoDataTableItem[]
      },
      events(): SoDataTableItem[] {
        return (this.eventsQuery?.data ?? []) as unknown as SoDataTableItem[]
      },
      isConfigFormValid(): boolean {
        return checkConfigFormValid(
          this.configForm as ConfigFormValues,
          !!this.editingConfig,
        )
      },
    },
    watch: {
      eventStatusFilter() {
        this.eventsQuery = EmailThrottleService.getEvents(
          this.eventStatusFilter ?? undefined,
        )
      },
      configs(list: SoDataTableItem[]) {
        if (this.deepLinkHandled || !this.deepLinkCommunicationId) return
        if (this.configsQuery?.isFetching) return
        this.deepLinkHandled = true
        const existing = (list as unknown as IThrottleConfig[]).find(
          (c) => c.communicationId === this.deepLinkCommunicationId,
        )
        if (existing) return // already configured — land on the page as-is
        this.openCreateDialog()
        this.configForm.communicationId = this.deepLinkCommunicationId
      },
    },
    created() {
      const { communicationId, communicationName } = this.$route.query
      this.deepLinkCommunicationId =
        typeof communicationId === "string" ? Number(communicationId) : null
      this.deepLinkCommunicationName =
        typeof communicationName === "string" ? communicationName : null
      this.createMutation = EmailThrottleMutator.createConfig(() => {
        this.showConfigDialog = false
        this.refreshAll()
      })
      this.updateMutation = EmailThrottleMutator.updateConfig(() => {
        this.showConfigDialog = false
        this.refreshAll()
      })
      this.resumeMutation = EmailThrottleMutator.resumeEvent(() => {
        this.showResumeDialog = false
        this.eventsQuery?.refetch()
      })
      this.discardMutation = EmailThrottleMutator.discardEvent(() => {
        this.showDiscardDialog = false
        this.eventsQuery?.refetch()
      })
    },
    mounted() {
      this.configsQuery = EmailThrottleService.getConfigs()
      this.eventsQuery = EmailThrottleService.getEvents(
        this.eventStatusFilter ?? undefined,
      )
    },
    methods: {
      refreshAll() {
        this.configsQuery?.refetch()
        this.eventsQuery?.refetch()
      },
      openCreateDialog() {
        this.editingConfig = null
        this.configForm = {
          communicationId: null,
          maxPerWindow: null,
          windowSeconds: 3600,
          enabled: true,
        }
        this.showConfigDialog = true
      },
      openEditDialog(config: IThrottleConfig) {
        this.editingConfig = config
        this.configForm = {
          communicationId: config.communicationId,
          maxPerWindow: config.maxPerWindow,
          windowSeconds: config.windowSeconds,
          enabled: config.enabled,
        }
        this.showConfigDialog = true
      },
      saveConfig() {
        if (this.editingConfig) {
          this.updateMutation?.mutate({
            id: this.editingConfig.id,
            maxPerWindow: this.configForm.maxPerWindow!,
            windowSeconds: this.configForm.windowSeconds!,
            enabled: this.configForm.enabled,
          })
        } else {
          this.createMutation?.mutate({
            communicationId: this.configForm.communicationId!,
            maxPerWindow: this.configForm.maxPerWindow!,
            windowSeconds: this.configForm.windowSeconds!,
          })
        }
      },
      confirmResume(event: IThrottleEvent) {
        this.pendingEventAction = event
        this.showResumeDialog = true
      },
      confirmDiscard(event: IThrottleEvent) {
        this.pendingEventAction = event
        this.showDiscardDialog = true
      },
      executeResume() {
        if (this.pendingEventAction) {
          this.resumeMutation?.mutate(this.pendingEventAction.id)
        }
      },
      executeDiscard() {
        if (this.pendingEventAction) {
          this.discardMutation?.mutate(this.pendingEventAction.id)
        }
      },
      formatWindow(seconds?: number): string {
        if (!seconds) return "—"
        if (seconds < 60) return `${seconds}s`
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
        return `${Math.floor(seconds / 3600)}h`
      },
      formatDate(date?: Dayjs | null): string {
        if (!date) return "—"
        return date.format("DD MMM YYYY HH:mm")
      },
      statusChipType(status: ThrottleEventStatus): SoChipType {
        if (status === ThrottleEventStatus.ACTIVE) return SoChipType.WARNING
        if (status === ThrottleEventStatus.RESUMED) return SoChipType.SUCCESS
        return SoChipType.DEFAULT
      },
    },
  })
</script>
